import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { allFields } from '@/lib/formSections'
import { crossFormKey } from '@/lib/crossForm'
import {
  TEMPLATE_NONE,
  pinnedInactiveTemplate,
  resolveTemplateId,
  selectableTemplates,
  type ContainingTemplate,
} from '@/lib/crossFormTemplate'
import type { CrossFormResolution } from '@/lib/crossForm'
import type { FormDefinition, ProcedureTemplateDetail } from '@/types/cgt'

/** A referenceable field on an upstream form (value-bearing, non-MR). */
export interface UpstreamField {
  id: string
  label: string
  /** cgt field type — 'dropdown', 'number', … (NOT the builder's 'select'). */
  fieldType: string
  /** Full ref key: `${handle}.${id}` — what gets stored in formulas/conditions. */
  refKey: string
}

/** One form strictly earlier than the current form in a Procedure Template. */
export interface UpstreamForm {
  handle: string
  name: string
  fields: UpstreamField[]
}

/**
 * Authoring-time data for cross-form references ({handle.fieldId}) in the form
 * builder: the Procedure Templates the current form belongs to, the forms
 * strictly EARLIER than it in the selected template (so circular references
 * cannot be authored), and label lookup for refs already stored in formulas.
 *
 * Unsaved forms (no id) belong to no template — `available` is false and the
 * cross-form UI simply doesn't render, per the decided behavior.
 */
export interface CrossFormAuthoring {
  /** True when the form is saved AND belongs to at least one template. */
  available: boolean
  /**
   * EVERY template containing the form, retired versions included — label
   * lookup, not a menu. Use `selectableProcedureTemplates` for anything the
   * user picks from.
   */
  procedureTemplates: ContainingTemplate[]
  /**
   * The templates the selector may OFFER: ACTIVE versions only, per criterion D
   * of 86eybvqgn. A form already pinned to a retired version still displays it
   * (`pinnedProcedureTemplate`); nobody can newly choose one.
   */
  selectableProcedureTemplates: ContainingTemplate[]
  /**
   * The stored template when it is one `selectableProcedureTemplates` no longer
   * lists, so the trigger can still show what the form is pinned to. Display
   * only — never an option.
   */
  pinnedProcedureTemplate: ContainingTemplate | null
  /**
   * The template context, or null = "None" (cross-form referencing off — only
   * the current form's fields are offered).
   *
   * With nothing stored this is the highest-numbered ACTIVE version of the
   * form's template family (z8nrz7dm2q). Display only: it is not written back,
   * so opening a form and closing it changes nothing.
   */
  selectedProcedureTemplateId: string | null
  selectProcedureTemplate: (id: string | null) => void
  /** Forms strictly earlier than the current form in the selected template. */
  upstreamForms: UpstreamForm[]
  /**
   * Upstream fields of the SELECTED template, flattened and labeled with their
   * form name — the global template context (toolbar picker) scopes every
   * cross-form surface: formula chips, visibility sources, re-map targets.
   */
  allUpstreamFields: UpstreamField[]
  loading: boolean
  /** "Label (Form Name)" for a stored cross ref, or null when unresolvable. */
  labelForRef: (ref: string) => string | null
  /**
   * Cross-form context shaped for FormRenderer in the BUILDER PREVIEW, where
   * there is no patient journey to read values from.
   *
   * Without this the preview passes no `crossForm` at all, so every cross-form
   * ref misses `fieldLabels` and renders as "⚠ deleted field" even when the
   * field is perfectly alive on the upstream form — the ref is namespaced
   * (`handle.fieldId`) and can never match a local field id.
   *
   * `values` is empty on purpose: nothing is filled in a preview, so a
   * calculated field correctly shows no value. What changes is that a
   * resolvable ref now reports `waiting`, which FormRenderer renders as
   * "Waiting for <Form> to be filled" — a hint about what will populate it,
   * rather than a false claim that the field was deleted.
   */
  previewResolution: CrossFormResolution
  /**
   * Why the last template-context save failed, or null. Not swallowed: the
   * point of persisting the choice is that it survives a reload, so a silent
   * failure would leave the author believing it had been saved.
   */
  contextSaveError: string | null
}

// Field types that may be referenced across forms: value-bearing, stored in
// submission.values. MR fields are excluded (their values live on the patient
// record, never in values) and so are layout/table/process/signature fields.
const REFERENCEABLE_TYPES = new Set([
  'text',
  'number',
  'date',
  'datetime',
  'textarea',
  'checkbox',
  'dropdown',
  'calculated',
])

function upstreamFieldsOf(def: FormDefinition, handle: string): UpstreamField[] {
  return allFields(def)
    .filter(f => REFERENCEABLE_TYPES.has(f.fieldType) && !f.isMrField)
    .map(f => ({
      id: f.id,
      label: f.label || 'Untitled',
      fieldType: f.fieldType,
      refKey: crossFormKey(handle, f.id),
    }))
}

// Step ids strictly upstream of `stepId` (transitive predecessors).
function ancestorStepIds(detail: ProcedureTemplateDetail, stepId: string): Set<string> {
  const predsByStep = new Map<string, string[]>()
  for (const s of detail.steps) predsByStep.set(s.stepId, s.predecessorStepIds)
  const ancestors = new Set<string>()
  const stack = [...(predsByStep.get(stepId) ?? [])]
  while (stack.length > 0) {
    const id = stack.pop()!
    if (ancestors.has(id)) continue
    ancestors.add(id)
    stack.push(...(predsByStep.get(id) ?? []))
  }
  return ancestors
}

export function useCrossFormAuthoring(
  current: { formId?: string; familyId?: string; crossFormTemplateId?: string | null } | null,
): CrossFormAuthoring {
  const formId = current?.formId
  // Three persisted shapes, three meanings: a template id; the literal string
  // 'none' (the author explicitly turned cross-form context off); and null /
  // absent (never chosen — the sole-template default may apply). One nullable
  // column cannot hold "explicit None" AND "never chosen" as the same value
  // without the explicit choice un-doing itself on reload, so None is stored
  // as its own sentinel rather than as null.
  const savedProcedureTemplateId = current?.crossFormTemplateId ?? null
  const procedureTemplatesQuery = useQuery({
    queryKey: ['form-definitions', formId, 'procedure-templates'],
    queryFn: () => formsApi.listFormProcedureTemplates(formId!),
    enabled: !!formId,
  })
  const procedureTemplates = useMemo<ContainingTemplate[]>(
    () =>
      (procedureTemplatesQuery.data?.items ?? []).map(p => ({
        id: p.id,
        name: p.name,
        version: p.version ?? null,
        // Both carried through rather than dropped here: `active` decides what
        // the dropdown may offer and `familyId` decides which versions are
        // even the same lineage, and neither can be recovered downstream.
        active: p.active,
        familyId: p.familyId ?? null,
        updatedAt: p.updatedAt,
      })),
    [procedureTemplatesQuery.data],
  )
  const selectableProcedureTemplates = useMemo(
    () => selectableTemplates(procedureTemplates),
    [procedureTemplates],
  )

  // 'none' = the author explicitly chose no template context; null = no
  // explicit choice yet (fall back to the default).
  //
  // Seeded from the value stored on the form, so a reload restores the choice
  // instead of silently reverting to the default — which, for a form sitting in
  // several templates, was None, and left every cross-form ref unresolvable.
  const [pickedProcedureTemplateId, setPickedProcedureTemplateId] = useState<string | 'none' | null>(
    savedProcedureTemplateId,
  )
  const [contextSaveError, setContextSaveError] = useState<string | null>(null)

  // FormEditorPage stays mounted across "Save as New Version": it navigates to
  // the new definition's id on the SAME route rather than remounting. A plain
  // "seed once" effect fires only for the FIRST form this component instance
  // ever shows, so the minted version — which starts detached, with a null
  // context — silently inherited the OLD version's picked template forever;
  // nothing the author did afterward could tell the difference from "still
  // attached".
  //
  // This is React's documented fix for exactly that shape of bug: detect the
  // identity change DURING render (not in an effect) and reset synchronously,
  // so there is no extra render showing the previous form's template before
  // the reset lands. https://react.dev/learn/you-might-not-need-an-effect
  const [seededFormId, setSeededFormId] = useState(formId)
  if (formId !== seededFormId) {
    setSeededFormId(formId)
    // 'none' seeds as the explicit-None state — NOT as null, which would let
    // the sole-template default re-enable a context the author turned off.
    // A genuinely fresh/detached form (savedProcedureTemplateId === null)
    // resets to null: no stale carryover, and the usual default below decides
    // what to show from there.
    setPickedProcedureTemplateId(
      savedProcedureTemplateId === null ? null : savedProcedureTemplateId,
    )
  }
  // A stored choice wins; only "never chosen" reaches the default. The rule
  // itself lives in `lib/crossFormTemplate.ts` so it can be tested without a
  // renderer — see that module for why the default never overrides.
  const selectedProcedureTemplateId = resolveTemplateId(
    pickedProcedureTemplateId,
    procedureTemplates,
  )
  const pinnedProcedureTemplate = pinnedInactiveTemplate(
    selectedProcedureTemplateId,
    procedureTemplates,
  )

  // Details for EVERY containing template (bounded: forms sit in few templates).
  // The selected one drives the browse UI; the union feeds visibility sources
  // and ref-label resolution.
  const detailsQuery = useQuery({
    queryKey: ['procedure-templates', 'details-for-form', procedureTemplates.map(p => p.id)],
    queryFn: async () => {
      const details = await Promise.all(procedureTemplates.map(p => procedureTemplatesApi.get(p.id)))
      return details
    },
    enabled: procedureTemplates.length > 0,
  })

  // All form definitions once — resolves upstream steps' defs and ref labels.
  const defsQuery = useQuery({
    queryKey: ['form-definitions', 'list-for-cross-refs'],
    queryFn: () => formsApi.listDefinitions(),
    enabled: procedureTemplates.length > 0,
  })

  const { upstreamForms, allUpstreamFields, refLabels, refFormNames } = useMemo(() => {
    const defs = defsQuery.data?.items ?? []
    const defsById = new Map(defs.map(d => [d.id, d]))

    // "Label (Form)" for any handle in the system — newest version wins so
    // labels track the current shape of each family.
    const refLabels = new Map<string, string>()
    const refFormNames = new Map<string, string>()
    const byHandle = [...defs].sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
    const seenHandles = new Set<string>()
    for (const def of byHandle) {
      if (!def.handle || seenHandles.has(def.handle)) continue
      seenHandles.add(def.handle)
      for (const f of upstreamFieldsOf(def, def.handle)) {
        refLabels.set(f.refKey, `${f.label} (${def.name})`)
        // Kept separately from the label: the preview's "waiting" note needs the
        // bare form name, not the "Label (Form)" composite.
        refFormNames.set(f.refKey, def.name)
      }
    }

    const details = detailsQuery.data ?? []
    const familyId = current?.familyId
    const perProcedureTemplate = new Map<string, UpstreamForm[]>()
    for (const detail of details) {
      const ownStep = detail.steps.find(
        s => (familyId && s.formFamilyId === familyId) || s.formDefinitionId === formId,
      )
      if (!ownStep) continue
      const ancestors = ancestorStepIds(detail, ownStep.stepId)
      const forms: UpstreamForm[] = []
      for (const step of detail.steps) {
        if (!ancestors.has(step.stepId)) continue
        const def = defsById.get(step.formDefinitionId)
        const handle = def?.handle ?? step.formHandle
        if (!def || !handle) continue
        forms.push({ handle, name: step.formName, fields: upstreamFieldsOf(def, handle) })
      }
      perProcedureTemplate.set(detail.id, forms)
    }

    const upstreamForms = selectedProcedureTemplateId
      ? perProcedureTemplate.get(selectedProcedureTemplateId) ?? []
      : []
    // Scoped to the selected template only — the toolbar context is the single
    // source of "which earlier forms are eligible" for every picker.
    const flat = new Map<string, UpstreamField>()
    for (const form of upstreamForms) {
      for (const f of form.fields) {
        if (!flat.has(f.refKey)) {
          flat.set(f.refKey, { ...f, label: `${f.label} (${form.name})` })
        }
      }
    }
    return { upstreamForms, allUpstreamFields: [...flat.values()], refLabels, refFormNames }
  }, [defsQuery.data, detailsQuery.data, selectedProcedureTemplateId, current?.familyId, formId])

  // Labels for every ref the system can resolve, plus a "waiting" state for
  // each. A ref that resolves is alive and simply unfilled; one that does not
  // is left absent, so it still reads as a deleted field — which is then true.
  const previewResolution = useMemo<CrossFormResolution>(() => {
    // Labels stay system-wide on purpose: naming a real field is always more
    // truthful than "deleted field", whatever the template context — that
    // fallback firing for live fields is the bug this exists to fix.
    //
    // The "waiting" promise is narrower. Only a ref the SELECTED template's
    // upstream forms can actually fill gets it; a ref that resolves somewhere
    // in the system but not upstream here reads 'ok' — labeled, no note —
    // because "waiting" would promise a value this journey cannot deliver.
    const upstreamKeys = new Set(allUpstreamFields.map((f) => f.refKey))
    return {
      values: {},
      labelById: Object.fromEntries(refLabels),
      stateByRef: Object.fromEntries(
        [...refFormNames].map(([ref, formName]) => [
          ref,
          upstreamKeys.has(ref)
            ? { status: 'waiting' as const, formName }
            : { status: 'ok' as const },
        ]),
      ),
      // The builder's preview: no run, so nothing is skipped.
      skippedHandles: [],
    }
  }, [refLabels, refFormNames, allUpstreamFields])

  return {
    available: !!formId && procedureTemplates.length > 0,
    procedureTemplates,
    selectableProcedureTemplates,
    pinnedProcedureTemplate,
    selectedProcedureTemplateId,
    selectProcedureTemplate: (id: string | null) => {
      setPickedProcedureTemplateId(id ?? TEMPLATE_NONE)
      // Persisted immediately rather than on form save: the choice is authoring
      // context, not schema, so updateMeta edits in place and never mints a
      // version. Saving it here is what makes it survive a reload even if the
      // author never saves the form itself.
      if (formId) {
        setContextSaveError(null)
        formsApi.updateDefinitionMeta(formId, { crossFormTemplateId: id ?? TEMPLATE_NONE }).catch((e: unknown) => {
          // Surfaced, not swallowed. The whole point of persisting is that the
          // choice survives a reload; a silent failure would put the author
          // back in the bug this fixes, believing it had been saved.
          setContextSaveError(
            e instanceof Error ? e.message : 'Could not save the cross-form template context.',
          )
        })
      }
    },
    upstreamForms,
    allUpstreamFields,
    loading: procedureTemplatesQuery.isLoading || detailsQuery.isLoading || defsQuery.isLoading,
    labelForRef: (ref: string) => refLabels.get(ref) ?? null,
    previewResolution,
    contextSaveError,
  }
}
